import React, { useEffect, useState } from 'react';
import './truck.css';

// Animert Tippi-lastebil. Geometri hentet fra logo/Tippi Logo Animations.html.
// All CSS ligger i truck.css, scopet under .tippi-truck.
const NAVY = '#1a1a2e';
const GOLD = '#fdc149';
const ERROR_RED = '#c93a3a';
const INK = { light: NAVY, dark: '#ffffff' };

const PILE = 'M77,99.514c11.589-19.7,26.654-25.5,42.878-24.336C126.831,58.953,138.42,52,151.168,52c15.065,0,24.336,6.953,30.131,16.224,10.039.3,14.419,8.1,13.756,16.173l-10.279,24.387H79.318C78.159,105.308,77,102.99,77,99.514Z';
const BODY = [
  ['M48,103H189.382', '-38.729 -74.028'],
  ['M49.271,103,40,152.831l42.966,17.013', '-40 -74.028'],
  ['M182,78h62.579', '-17.441 -78'],
  ['M187.878,78,145,195.046', '-23.319 -78'],
  ['M253.426,129.325H200.459V94.51c4.621,0,15.613-.074,21.269,0,4.359,0,34.983,4,34.983,34.815v63.1', '-14.508 -75.382'],
  ['M236,179h15.065', '-8.862 -61.954'],
  ['M60.748,181.737A12.861,12.861,0,0,1,48,168.99v-7.219', '-42.206 -64.692'],
];
const STRIPES = ['-100.711', '-72.711', '-44.711'];

const DEFAULT_LABELS = {
  load: 'Laster',
  empty: 'Ingen treff',
  success: 'Ferdig',
  fail: 'Noe gikk galt',
  intro: 'Tippi',
  drive: 'På vei',
};

const LOOP_MS = 4200;

function Badge({ children, fill }) {
  return (
    <g className="badge">
      <circle cx="262" cy="30" r="26" fill={fill} />
      {children}
    </g>
  );
}

function Check() {
  return (
    <Badge fill={GOLD}>
      <path d="M250 30l9 9 16-18" fill="none" stroke={NAVY} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
    </Badge>
  );
}

function Alert() {
  return (
    <Badge fill={ERROR_RED}>
      <path d="M262 17v15" stroke="#fff" strokeWidth="7" strokeLinecap="round" />
      <circle cx="262" cy="43" r="4" fill="#fff" />
    </Badge>
  );
}

function extras(variant) {
  switch (variant) {
    case 'success':
      return {
        post: (
          <>
            <Check />
            {[0, 60, 120, 180, 240, 300].map((a, i) => (
              <g key={a} transform={`translate(262 30) rotate(${a})`}>
                <line className="spark" style={{ animationDelay: `${(i % 2) * 0.06}s` }}
                  x1="34" x2="46" y1="0" y2="0" stroke={GOLD} strokeWidth="5" strokeLinecap="round" />
              </g>
            ))}
          </>
        ),
      };
    case 'fail':
      return {
        pre: [0, 0.8, 1.6].map((d, i) => (
          <circle key={d} className="smoke" style={{ '--d': `${d}s` }} cx={240 + i * 12} cy="96" r="7" />
        )),
        post: <Alert />,
      };
    case 'empty':
      return {
        root: (
          <g className="stone" fill={NAVY}>
            <path d="M440,366 l9,-6 10,3 3,9 -7,7 -12,-1 z" />
          </g>
        ),
      };
    case 'drive':
      return {
        pre: (
          <line className="road" x1="-40" x2="300" y1="246" y2="246" stroke="currentColor" strokeWidth="5" strokeLinecap="round" opacity=".4" />
        ),
        post: [60, 80, 100].map((y, i) => (
          <line key={y} className="speed" style={{ animationDelay: `${i * 0.15}s` }}
            x1="-6" x2="14" y1={y + i * 8} y2={y + i * 8} stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
        )),
      };
    default:
      return {};
  }
}

export default function TruckAnimation({
  variant = 'load', theme = 'light', size = 96, label, className, style, decorative = false, loop = false,
}) {
  const intro = variant === 'intro';
  // intro spilles én gang ved mount; med loop remountes svg-en for å starte på nytt.
  const [round, setRound] = useState(0);
  useEffect(() => {
    if (!intro || !loop) return undefined;
    const id = setInterval(() => setRound(r => r + 1), LOOP_MS);
    return () => clearInterval(id);
  }, [intro, loop]);

  const ink = INK[theme] ?? INK.light;
  const x = extras(variant);
  const text = label ?? DEFAULT_LABELS[variant] ?? '';
  const a11y = decorative
    ? { 'aria-hidden': 'true' }
    : { role: variant === 'load' ? 'status' : 'img', 'aria-label': text };

  return (
    <span
      className={['tippi-truck', `tippi-truck--${variant}`, intro && 'play', className].filter(Boolean).join(' ')}
      style={{ width: size, color: ink, ...style }}
      {...a11y}
    >
      <svg key={round} viewBox="91 120 330 270" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
        {x.root}
        <g transform="translate(94.898 114.907)">
          {x.pre}
          <g className="truck">
            <g className="top">
              <g className="pile">
                <path d={PILE} transform="translate(1.243 -7)" fill={GOLD} />
              </g>
              <g transform="translate(40 83.243)" fill="none" stroke={ink} strokeLinecap="round" strokeLinejoin="round" strokeWidth="10">
                {BODY.map(([d, t], i) => (intro
                  ? <path key={d} className="draw" pathLength="1" style={{ '--d': `${i * 0.09}s` }} d={d} transform={`translate(${t})`} />
                  : <path key={d} d={d} transform={`translate(${t})`} />))}
              </g>
              <g transform="translate(82.878 128.439)" fill="none" stroke={GOLD} strokeLinecap="round" strokeWidth="10">
                {STRIPES.map((s, i) => (
                  <g key={s} className="stripe" style={{ '--d': `${intro ? 0.55 + i * 0.1 : i * 0.18}s` }}>
                    <path d="M114.43,124,104,151.813" transform={`translate(${s} -124)`} />
                  </g>
                ))}
              </g>
            </g>
            <g transform="translate(72.448 172.476)" fill="none" stroke={ink} strokeWidth="10">
              {[0.153, 123.153].map((px, wi) => (
                <g key={px} transform={`translate(${px} -0.289)`}>
                  {intro
                    ? <circle className="draw wheel" pathLength="1" style={{ '--d': `${0.35 + wi * 0.15}s` }} cx="32.5" cy="32.5" r="32.5" transform="rotate(-90 32.5 32.5)" />
                    : <circle className="wheel" cx="32.5" cy="32.5" r="32.5" />}
                </g>
              ))}
            </g>
          </g>
          {x.post}
        </g>
      </svg>
    </span>
  );
}
